import React from 'react';
import { Link } from 'react-router';
import { useAuth } from '../../contexts/AuthContext';
import { useCms } from '../../contexts/CmsContext';
import { Sparkles, ChevronUp, MessageSquare, Gift, GraduationCap } from 'lucide-react';

export default function Footer(): React.JSX.Element {
  const cms = useCms();
  const { isAuthenticated } = useAuth();

  const siteName = cms.get('site_name', 'EcoleMinds');
  const tagline = cms.get('footer_tagline', 'From spreadsheet warrior to AI-powered finance professional.');
  const copyright = cms.get('footer_copyright', 'Built with love for finance professionals who want to lead the AI transformation.');

  const learnLinks = [
    { label: 'Courses', to: '/courses' },
    { label: 'Workshops', to: '/workshops' },
    { label: 'AI News', to: '/news' },
    { label: 'Blog', to: '/blog' },
    { label: 'Mentor', to: '/mentor' },
  ];

  const companyLinks = [
    { label: 'About Us', to: '/about' },
    { label: 'Contact', to: '/contact' },
    { label: 'Terms & Conditions', to: '/terms' },
    { label: 'Privacy Policy', to: '/privacy' },
    { label: 'Refund Policy', to: '/refund-policy' },
  ];

  const accountLinks = [
    { label: 'My Courses', to: '/my-courses' },
    { label: 'Wishlist', to: '/wishlist' },
    { label: 'My Notes', to: '/notes' },
    { label: 'Refer & Earn', to: '/referral', icon: Gift },
    { label: 'Teach with Us', to: '/trainer/apply', icon: GraduationCap },
    { label: 'Profile', to: '/profile' },
  ];

  return (
    <footer className="bg-white border-t border-slate-100 py-16 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-8 mb-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900">{siteName}</span>
            </div>
            <p className="text-slate-600 text-sm leading-relaxed mb-4 max-w-sm">
              &ldquo;{tagline}&rdquo;
            </p>
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <MessageSquare className="w-3.5 h-3.5" />
              <span>AI-powered career guidance available 24/7</span>
            </div>
          </div>

          {/* Learn */}
          <div className="col-span-1 md:col-span-2">
            <h4 className="text-sm font-bold text-slate-900 mb-4">Learn</h4>
            <div className="space-y-2.5">
              {learnLinks.map(item => (
                <Link
                  key={item.label}
                  to={item.to}
                  className="block text-sm text-slate-600 hover:text-amber-500 transition-colors"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Company */}
          <div className="col-span-1 md:col-span-2">
            <h4 className="text-sm font-bold text-slate-900 mb-4">Company</h4>
            <div className="space-y-2.5">
              {companyLinks.map(item => (
                <Link
                  key={item.label}
                  to={item.to}
                  className="block text-sm text-slate-600 hover:text-amber-500 transition-colors"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Account */}
          {isAuthenticated && (
            <div className="col-span-2 md:col-span-2">
              <h4 className="text-sm font-bold text-slate-900 mb-4">Account</h4>
              <div className="space-y-2.5">
                {accountLinks.map(item => (
                  <Link
                    key={item.label}
                    to={item.to}
                    className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-amber-500 transition-colors"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* For guests, show CTA in that column */}
          {!isAuthenticated && (
            <div className="col-span-2 md:col-span-2">
              <h4 className="text-sm font-bold text-slate-900 mb-4">Get Started</h4>
              <div className="space-y-2.5">
                <Link to="/login" className="block text-sm text-slate-600 hover:text-amber-500 transition-colors">
                  Sign In
                </Link>
                <Link to="/courses" className="block text-sm text-slate-600 hover:text-amber-500 transition-colors">
                  Browse Courses
                </Link>
                <Link to="/mentor" className="block text-sm text-slate-600 hover:text-amber-500 transition-colors">
                  Ask Mentor
                </Link>
                <Link to="/trainer/apply" className="block text-sm text-slate-600 hover:text-amber-500 transition-colors">
                  Teach with Us
                </Link>
              </div>
            </div>
          )}
        </div>

        <div className="pt-8 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500">{copyright}</p>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="p-2 rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
            aria-label="Back to top"
          >
            <ChevronUp className="w-5 h-5" />
          </button>
        </div>
      </div>
    </footer>
  );
}
